import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useSession } from '@/shared/auth/SessionProvider';
import { setActiveCountryHeader } from '@/shared/api/client';
import { COUNTRIES, isSupportedCountry, type CountryCode, type CountryConfig } from './index';

/**
 * Active country state. Defaults to the user's `pais_codigo` on login;
 * cross-region admins (`paises_permitidos.length > 1`) can override via
 * the country switcher in `AppShell`. Persisted in localStorage so a
 * reload doesn't reset the choice.
 *
 * Design — purely derived `code`:
 *
 *   code = userOverride ?? resolveActive(profile)
 *
 * `userOverride` is the only React state piece. setCountry just writes
 * to it; the new code is then re-derived on the next render. There's
 * no separate `[code, setCode]` state, which means there's no window
 * where the React state lags behind the derived value (the bug the
 * previous "set state during render" pattern was trying to plug — and
 * still left a race when `resolveActive` legitimately produced a value
 * that disagreed with a freshly-set `code` during the same render
 * sequence, manifesting as "switching to MX does nothing").
 *
 * The `X-Active-Country` header on the API client is mirrored during
 * render (idempotent module-level write) so a child component's fetch
 * cannot fire with a stale header between render and an effect-flush.
 * localStorage persistence runs in a useEffect since it has observable
 * cross-tab side effects.
 *
 * Switching country also `clear()`s the React Query cache so AR data
 * cannot render inside an MX session (and vice versa) — query keys
 * aren't country-prefixed everywhere, so we can't rely on isolation by
 * key alone.
 */

const STORAGE_KEY = 'fornitalia.active_pais';

type ActiveCountryContextValue = {
  /** Current active country code. */
  code: CountryCode;
  /** Resolved config for the current country. */
  config: CountryConfig;
  /** Codes the user is allowed to switch to. Always includes `code`. */
  available: CountryCode[];
  /** True iff the user has access to more than one country. */
  canSwitch: boolean;
  /** Change the active country. Throws if the code isn't permitted. */
  setCountry: (code: CountryCode) => void;
};

const ActiveCountryContext = createContext<ActiveCountryContextValue | null>(null);

function readPersisted(): CountryCode | null {
  if (typeof window === 'undefined') return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v && isSupportedCountry(v) ? v : null;
}

function writePersisted(code: CountryCode | null) {
  if (typeof window === 'undefined') return;
  if (code) window.localStorage.setItem(STORAGE_KEY, code);
  else window.localStorage.removeItem(STORAGE_KEY);
}

type Profile = ReturnType<typeof useSession>['profile'];

function resolveActive(p: Profile): CountryCode {
  // Nova is an MX-only perfil — even if the user record happens to
  // carry a wider `paises_permitidos` (test accounts get the
  // admin-style ['AR','MX'] by default), force MX so Nova never lands
  // on a country where Administración renders empty. The persisted
  // localStorage value from a prior admin session would otherwise pass
  // the `permitted.includes(persisted)` gate below and drop Nova onto
  // AR.
  if (p?.perfil === 'nova') return 'MX';
  const permitted = p?.paises_permitidos ?? [];
  const persisted = readPersisted();
  if (persisted && permitted.includes(persisted)) return persisted;
  if (p?.pais_codigo && permitted.includes(p.pais_codigo)) {
    return p.pais_codigo;
  }
  return permitted[0] ?? 'AR';
}

export function ActiveCountryProvider({ children }: { children: ReactNode }) {
  const { profile } = useSession();
  const queryClient = useQueryClient();

  // The user's explicit in-session country pick via the CountrySwitcher.
  // Sole source of React-tracked state on this provider; everything
  // else (`code`, `available`) is derived. `setCountry` writes here,
  // and a profile-identity change resets it so a different user can't
  // inherit the previous user's switch within the same browser session.
  const [userOverride, setUserOverride] = useState<CountryCode | null>(null);

  // Reset the override when the profile identity changes (sign-out
  // + new sign-in, or a different user via fetchMe). Same-user reloads
  // mount the provider fresh, so userOverride is already null then —
  // the persisted localStorage value carries their last switch
  // forward via resolveActive instead.
  const lastProfileIdRef = useRef<string | null>(profile?.id ?? null);
  if (lastProfileIdRef.current !== (profile?.id ?? null)) {
    lastProfileIdRef.current = profile?.id ?? null;
    if (userOverride !== null) setUserOverride(null);
  }

  // Pure derivation. No useState for `code` means there's no window
  // where state and derivation disagree — every render that consumes
  // `code` sees the same value that `resolveActive` would yield given
  // the current profile + override.
  const code: CountryCode = userOverride ?? resolveActive(profile);

  // Mirror to the API client during render. Idempotent (same code
  // means same assignment), safe under Strict Mode's double-invoke.
  // Doing this here instead of in a useEffect means children see a
  // header that matches the code in the same commit — no fetch can
  // fire with a stale header between render and effect-flush.
  setActiveCountryHeader(profile ? code : null);

  // Persist the resolved code so a reload (or a different tab) keeps
  // the user's view. localStorage writes are best-effort side effects
  // — kept in an effect rather than during render so any storage-event
  // listeners fire after commit, not during.
  useEffect(() => {
    if (profile) writePersisted(code);
  }, [code, profile]);

  const setCountry = useCallback(
    (next: CountryCode) => {
      const rawPermitted = profile?.paises_permitidos ?? [];
      // Mirror resolveActive's Nova clamp so the CountrySwitcher can't
      // bypass the MX-only rule by issuing a direct setCountry call
      // (test accounts may carry a wider permitted set than spec).
      const permitted =
        profile?.perfil === 'nova'
          ? rawPermitted.filter((c) => c === 'MX')
          : rawPermitted;
      if (!permitted.includes(next)) {
        throw new Error(`País ${next} no permitido para este usuario`);
      }
      setUserOverride(next);
      // `clear()` is the right primitive on a country switch:
      //   1. wipes every cached query and observer state so AR data
      //      cannot render inside MX (or vice versa) — query keys are
      //      not country-prefixed everywhere, so we cannot rely on
      //      isolation by key alone.
      //   2. forces every active query to enter pending state and
      //      refetch with the new `X-Active-Country` header — no
      //      stale flash of old-country data while the new fetch is
      //      in flight (which is what `resetQueries()` allows).
      // We previously used `resetQueries()`, but that kept observers'
      // last data visible during the refetch — long enough for users
      // to see AR contacts inside an MX session.
      //
      // Supabase Realtime channels: no code needed here. They
      // authenticate via the JWT (unchanged on switch), stay
      // subscribed to their tables, and any postgres_changes event
      // fires `invalidateQueries` in `useRealtimeInvalidate`, which
      // then refetches under the new scope.
      queryClient.clear();
    },
    [profile, queryClient],
  );

  const value = useMemo<ActiveCountryContextValue>(() => {
    const rawPermitted = profile?.paises_permitidos ?? [code];
    // Nova's switchable set is hard-clamped to MX. Without this a
    // Nova test account configured with both AR + MX would render the
    // CountrySwitcher with an "Argentina" option that, if picked,
    // would land them on a broken empty Administración view.
    const permitted =
      profile?.perfil === 'nova'
        ? rawPermitted.filter((c) => c === 'MX')
        : rawPermitted;
    return {
      code,
      config: COUNTRIES[code],
      available: permitted,
      canSwitch: permitted.length > 1,
      setCountry,
    };
  }, [code, profile, setCountry]);

  return (
    <ActiveCountryContext.Provider value={value}>{children}</ActiveCountryContext.Provider>
  );
}

export function useActiveCountry(): ActiveCountryContextValue {
  const ctx = useContext(ActiveCountryContext);
  if (!ctx) throw new Error('useActiveCountry must be used inside <ActiveCountryProvider>');
  return ctx;
}
